import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Campaign, LineItemUnderInvestigation, PCADisplayType } from 'c-sdk';
import { PCAReportMetric, PermissionName } from 'c-sdk';
import type { TabWrapperSettings } from 'c-types';
import CampaignDisplayIssues from 'c-main/Components/Campaign/CampaignDisplayIssues';
import CampaignAnalytics from 'c-main/Components/Campaign/CampaignAnalytics';
import { TabsWrapper } from 'c-components';
import CampaignReportsWrapper from 'c-main/Components/Campaign/CampaignReports/CampaignReportsWrapper';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';

import EsEnergyUsage from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/EnergyUsage';
import DspReporting from 'c-main/Components/Campaign/CampaignReportingWrapper/DspReporting/DspReporting';
import UkDspReporting from 'c-main/Components/Campaign/CampaignReportingWrapper/DspReporting/UkDspReporting';
import FilteredCadExportButton from 'c-main/Components/Campaign/CampaignReportingWrapper/FilteredCadExportButton';

import { useUrlState, useUrlStateValue } from 'c-hooks';

import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import to from 'await-to-js';
import {
    geopathData,
    non_geopath_data,
} from 'c-main/Components/Campaign/CampaignReportingWrapper/mockData';
import CampaignAnalyticsTab from 'c-main/Components/Campaign/CampaignReports/Views/CampaignAnalyticsTab';

import HappyDemicsTabContent from 'c-main/Components/HappyDemics/HappyDemicsTabContent';
import { COMBINED_REPORT_METRIC, CombinedReport } from 'c-main/Components/Campaign/CombinedReport';
import {
    CLASSIC_POSTINGS_METRIC,
    ClassicPostings,
    useCanSeeClassicPostings,
} from 'c-main/Components/Campaign/ClassicPostings';
import { useUserPermissions } from 'c-auth-module/Hooks';
import TempHappydemicsPdfImages from 'c-main/Components/HappyDemics/TempHappydemicsPdf';
import { useAtom } from 'jotai';
import { atom_useLinearReportingOverride } from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import { LineItemsUnderInvestigationButton, OrderlineErrorsButton } from './TalonIssueButtons';

type Props = {
    id: number;
    campaign: Campaign;
};

const reportsPerms = [PermissionName.ReportingPcareportRead];
const adminPerms = [PermissionName.Admin];
const displayVerificationPermissions = [PermissionName.DisplaysDisplay_issuesRead];
const cadExportPerms = [PermissionName.Admin];

const CampaignReportingWrapper: React.FC<Props> = ({ id, campaign }) => {
    const { environmentMode, source, hasPCAData } = useCampaignWrapperState(id);
    const { hasAll } = useUserPermissions();
    const pcaKey = useMemo(() => `${environmentMode}-${source}`, [environmentMode, source]);

    const { start, data } = useAPIClientRequest(ApiClient.Entities.DataStatusLineItems.list);

    const getDsLineItems = useCallback(async () => {
        await to(
            start({
                perPage: 100000,
                filters: {
                    'filter.campaign_id': [id],
                    'filter.impacts_percentage': ['<1'],
                    'filter.expected_plays': ['>0'],
                    'filter.status': [0],
                },
            }),
        );
    }, [start, id]);

    useEffect(() => {
        if (campaign.is_talon) {
            getDsLineItems();
        }
    }, [getDsLineItems, campaign.is_talon]);

    const filterExtras = useMemo(() => {
        const extras: React.ReactNode[] = [];
        if (campaign.is_talon) {
            if (data?.data?.data?.length > 0) {
                extras.push(
                    <LineItemsUnderInvestigationButton
                        key="line-items-under-investigation"
                        rows={data.data.data as unknown as LineItemUnderInvestigation[]}
                    />,
                );
            }
            if (campaign?.campaignOrderlineSitelistErrors?.length > 0) {
                extras.push(
                    <OrderlineErrorsButton
                        key="orderline-errors"
                        errors={campaign.campaignOrderlineSitelistErrors}
                    />,
                );
            }
        }
        if (hasAll(cadExportPerms)) {
            extras.push(<FilteredCadExportButton key="filtered-cad-export" id={id} />);
        }
        return extras;
    }, [
        hasAll,
        id,
        campaign?.campaignOrderlineSitelistErrors,
        campaign.is_talon,
        data?.data?.data,
    ]);

    const { push, replaceMany } = useUrlState();
    const currentType = useUrlStateValue<'digital' | 'classic' | null>('type', null);
    const currentMetric = useUrlStateValue<string | null>('metric', null);
    const canSeeClassicPostings = useCanSeeClassicPostings(campaign);

    const availableTypes = useMemo<ReadonlyArray<PCADisplayType>>(
        () => (environmentMode ? campaign?.pca_display_types?.[environmentMode] ?? [] : []),
        [campaign?.pca_display_types, environmentMode],
    );
    const hasKnownDisplayTypesForEnvironment = useMemo(
        () =>
            Boolean(
                environmentMode &&
                    campaign?.pca_display_types &&
                    Object.prototype.hasOwnProperty.call(
                        campaign.pca_display_types,
                        environmentMode,
                    ),
            ),
        [campaign?.pca_display_types, environmentMode],
    );

    const hasDigitalRaw = availableTypes.includes('Display');
    const hasDigital =
        hasDigitalRaw || (hasKnownDisplayTypesForEnvironment && availableTypes.length === 0);
    const hasClassic = availableTypes.includes('Classic');

    const classicTabs = useMemo<TabWrapperSettings['tabs']>(
        () => [
            {
                title: 'Summary',
                urlName: 'summary',
                className: 'pca-summary',
                tabPanelStyling: { py: 0 },
                content: <CampaignAnalytics id={id} key={pcaKey} />,
            },
            {
                title: 'Modules.Main.Campaigns.Overview.Analytics.displayDaysTitle',
                urlName: 'display',
                className: 'pca-display',
                tabPanelStyling: { py: 0 },
                content: (
                    <CampaignReportsWrapper id={id} metric={PCAReportMetric.Display} key={pcaKey} />
                ),
            },
            {
                title: 'Impacts',
                urlName: 'impacts',
                className: 'pca-impacts',
                tabPanelStyling: { py: 0 },
                content: (
                    <CampaignReportsWrapper id={id} metric={PCAReportMetric.Impacts} key={pcaKey} />
                ),
            },
        ],
        [id, pcaKey],
    );

    const {
        start: startVistar,
        error: vistarError,
        isLoading: vistarLoading,
    } = useAPIClientRequest(ApiClient.Reports.impressionMultipliers);

    const {
        start: startGeo,
        error: geoError,
        isLoading: geoLoading,
    } = useAPIClientRequest(ApiClient.Reports.geopathImpressionMultipliers);

    const [vistarRows, setVistarRows] = useState<any[] | null>(null);
    const [geoRows, setGeoRows] = useState<any[] | null>(null);

    const mapVistar = useCallback(
        (rows: any[]) =>
            rows.map((row: any) => ({
                'Display Owner': row.display_owner,
                'Display Format': row.display_format,
                Plays: row.sum_of_play,
                'DSP Impressions': row.dsp_impressions,
                'DSP IM': row.dsp_avg_impressions,
                'Average IM': row.average_im,
                'IM VAR': row.variance,
                '% VAR': row.variance_percent * 100,
            })),
        [],
    );

    const mapGeo = useCallback(
        (rows: any[]) =>
            rows.map((row: any) => ({
                'Display Owner': row.display_owner,
                'Display Format': row.display_format,
                Plays: row.sum_of_play,
                'DSP Impressions': row.dsp_impressions,
                'Geopath Impressions': row.geopath_impressions,
                VAR: row.variance,
                'DSP IM': row.dsp_avg_impressions,
                'Geopath IM': row.geopath_avg_impressions,
                'IM VAR': row.im_variance,
                '% VAR': row.variance_percent * 100,
            })),
        [],
    );

    const showRoute = useMemo(
        () => campaign.type === 'vistar_uk' || campaign.type === 'hivestack_uk',
        [campaign.type],
    );

    const campaignHasDspReporting = useMemo(() => {
        if (id === 8896) return false;

        if (campaign.type === 'linear' || campaign.type === 'default') {
            return id === 9464 || id === 10160;
        }

        return true;
    }, [id, campaign.type]);
    const [useLinearReportingOverride] = useAtom(atom_useLinearReportingOverride);
    const shouldShowNewDSPReporting = campaignHasDspReporting && !useLinearReportingOverride;

    // A DSP campaign viewed in linear mode (override on) still offers the legacy
    // Vistar/Geopath impression-multiplier tables as a "DSP" tab.
    const shouldShowLegacyDSPTab = useMemo(
        () =>
            campaignHasDspReporting &&
            useLinearReportingOverride &&
            hasAll([PermissionName.ImpressionMultiplierRead]),
        [campaignHasDspReporting, useLinearReportingOverride, hasAll],
    );

    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!shouldShowLegacyDSPTab) {
                setVistarRows([]);
                setGeoRows([]);
                return;
            }

            if (id === 9464) {
                setVistarRows(non_geopath_data);
                setGeoRows(geopathData);
                return;
            }

            const [vErr, vRes] = await to(startVistar({ id, environment: environmentMode }));
            const [gErr, gRes] = await to(startGeo({ id, environment: environmentMode }));
            if (cancelled) return;

            if (!vErr && vRes?.data) setVistarRows(mapVistar((vRes as any).data));
            else setVistarRows([]);

            if (!gErr && gRes?.data) setGeoRows(mapGeo((gRes as any).data));
            else setGeoRows([]);
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldShowLegacyDSPTab, id, environmentMode, startVistar, startGeo, mapVistar, mapGeo]);

    const isDspLoading = useMemo(() => vistarLoading || geoLoading, [vistarLoading, geoLoading]);

    const digitalTabs = useMemo<TabWrapperSettings['tabs']>(() => {
        const theTabs = [
            {
                title: 'Modules.Main.Campaigns.Overview.Tabs.analyticsSummaryReports',
                permissions: reportsPerms,
                content: <CampaignAnalytics id={id} key={pcaKey} filterExtras={filterExtras} />,
                tabPanelStyling: { py: 0 },
                urlName: 'summary',
                className: 'pca-summary',
            },
            {
                title: 'Modules.Main.Campaigns.Overview.Tabs.impactsReports',
                permissions: reportsPerms,
                content: (
                    <CampaignReportsWrapper
                        id={id}
                        metric={PCAReportMetric.Impacts}
                        key={pcaKey}
                        filterExtras={filterExtras}
                    />
                ),
                tabPanelStyling: { py: 0 },
                urlName: 'impacts',
                className: 'pca-impacts',
            },
            {
                title: 'Modules.Main.Campaigns.Overview.Tabs.playsReports',
                permissions: reportsPerms,
                content: (
                    <CampaignReportsWrapper
                        id={id}
                        metric={PCAReportMetric.Plays}
                        key={pcaKey}
                        filterExtras={filterExtras}
                    />
                ),
                tabPanelStyling: { py: 0 },
                urlName: 'plays',
                className: 'pca-plays',
            },
            {
                title: 'Modules.Main.Campaigns.Overview.Tabs.timeReports',
                permissions: reportsPerms,
                content: (
                    <CampaignReportsWrapper
                        id={id}
                        metric={PCAReportMetric.Time}
                        key={pcaKey}
                        filterExtras={filterExtras}
                    />
                ),
                tabPanelStyling: { py: 0 },
                urlName: 'time',
                className: 'pca-time',
            },
        ];

        if (campaign.energy_efficiency_enabled) {
            theTabs.push({
                title: 'Modules.Main.Campaigns.Overview.Tabs.energyUsage',
                permissions: reportsPerms,
                content: <EsEnergyUsage id={id} key={pcaKey} />,
                tabPanelStyling: { py: 0 },
                urlName: 'energy',
                className: 'pca-energy',
            });
        }

        if (campaign.display_verification_enabled) {
            theTabs.push({
                title: 'Modules.Main.Campaigns.Overview.Tabs.displayVerification',
                permissions: displayVerificationPermissions,
                content: <CampaignDisplayIssues id={id} />,
                tabPanelStyling: { py: 0 },
                urlName: 'display_verf',
                className: 'pca-display-verf',
            });
        }

        if (shouldShowLegacyDSPTab) {
            theTabs.push({
                title: 'Modules.Main.Campaigns.Overview.Tabs.dsp',
                content: (
                    <DspReporting
                        id={id}
                        showRoute={showRoute}
                        vistarRows={vistarRows ?? []}
                        geoRows={geoRows ?? []}
                        vistarError={vistarError}
                        geoError={geoError}
                        isLoading={isDspLoading}
                    />
                ),
                tabPanelStyling: { py: 0 },
                urlName: 'dsp',
                className: 'dsp',
                permissions: [PermissionName.ImpressionMultiplierRead],
            });
        }

        if (id === 8896) {
            theTabs.push({
                title: 'Modules.Main.Campaigns.Overview.Tabs.dsp',
                content: <UkDspReporting />,
                tabPanelStyling: { py: 0 },
                urlName: 'dsp',
                className: 'dsp',
                permissions: adminPerms,
            });
        }
        return theTabs;
    }, [
        id,
        pcaKey,
        filterExtras,
        campaign.energy_efficiency_enabled,
        campaign.display_verification_enabled,
        shouldShowLegacyDSPTab,
        showRoute,
        vistarRows,
        geoRows,
        vistarError,
        geoError,
        isDspLoading,
    ]);

    const classicKeys = useMemo(
        () => new Set((classicTabs ?? []).map(t => t.urlName).filter(Boolean) as string[]),
        [classicTabs],
    );
    const digitalKeys = useMemo(
        () => new Set((digitalTabs ?? []).map(t => t.urlName).filter(Boolean) as string[]),
        [digitalTabs],
    );

    const typeTabs = useMemo<TabWrapperSettings['tabs']>(() => {
        const tabs: TabWrapperSettings['tabs'] = [];
        if (hasDigital) {
            tabs.push({
                title: 'Digital',
                urlName: 'digital',
                content: (
                    <TabsWrapper
                        tabs={digitalTabs}
                        type="secondary"
                        urlStatePath="metric"
                        key={`${pcaKey}-digital`}
                    />
                ),
            });
        }
        if (hasClassic) {
            tabs.push({
                title: 'Classic',
                urlName: 'classic',
                content: (
                    <TabsWrapper
                        tabs={classicTabs}
                        type="secondary"
                        urlStatePath="metric"
                        key={`${pcaKey}-classic`}
                    />
                ),
            });
        }
        return tabs;
    }, [hasDigital, hasClassic, digitalTabs, classicTabs, pcaKey]);

    const dspTabs = useMemo<TabWrapperSettings['tabs']>(() => {
        const tabs: TabWrapperSettings['tabs'] = [
            {
                title: 'Summary',
                urlName: 'summary',
                className: 'pca-summary',
                tabPanelStyling: { py: 0 },
                content: <CampaignAnalytics id={id} key={pcaKey} />,
            },
            {
                title: 'Analysis',
                urlName: 'analysis',
                className: 'pca-analysis',
                tabPanelStyling: { py: 0 },
                content: <CampaignAnalyticsTab id={id} />,
            },
        ];

        return tabs;
    }, [id, pcaKey]);

    useEffect(() => {
        if (shouldShowNewDSPReporting) return;
        if (!currentType) return;
        const validSet = currentType === 'digital' ? digitalKeys : classicKeys;
        if (!validSet.has(currentMetric ?? 'summary')) {
            // Correcting the URL, not navigating: replace so Back doesn't loop.
            replaceMany({ metric: 'summary' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentType, currentMetric, digitalKeys, classicKeys]);

    useEffect(() => {
        if (shouldShowNewDSPReporting) return;
        if (!currentType) return;
        if (currentType === 'digital' && !hasDigital && hasClassic) {
            replaceMany({ type: 'classic', metric: 'summary' });
        }
        if (currentType === 'classic' && !hasClassic && hasDigital) {
            replaceMany({ type: 'digital', metric: 'summary' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentType, hasDigital, hasClassic, shouldShowNewDSPReporting]);

    useEffect(() => {
        if (!shouldShowNewDSPReporting) return;
        if (!currentType) return;

        replaceMany({ type: null });
    }, [currentType, replaceMany, shouldShowNewDSPReporting]);

    if (!currentType && canSeeClassicPostings && currentMetric === COMBINED_REPORT_METRIC) {
        return <CombinedReport campaign={campaign} />;
    }

    if (!currentType && canSeeClassicPostings && currentMetric === CLASSIC_POSTINGS_METRIC) {
        return <ClassicPostings campaignId={id} />;
    }

    if (!currentType && hasAll(reportsPerms) && currentMetric === 'brandUpliftStudy') {
        return <HappyDemicsTabContent campaign={campaign} />;
    }

    if (!currentType && hasAll(reportsPerms) && currentMetric === 'conversionmeasurement') {
        return <TempHappydemicsPdfImages />;
    }

    if (shouldShowNewDSPReporting) {
        return (
            <TabsWrapper
                key="dsp-reporting"
                tabs={dspTabs}
                type="secondary"
                urlStatePath="metric"
            />
        );
    }

    return (
        <TabsWrapper
            key="linear-reporting"
            onTabChange={(newType: string) => {
                const validSet = newType === 'digital' ? digitalKeys : classicKeys;
                if (!validSet.has(currentMetric ?? 'summary')) {
                    push('metric', 'summary');
                }
            }}
            tabs={typeTabs}
            hideTabs
            type="secondary"
            urlStatePath="type"
        />
    );
};

export default CampaignReportingWrapper;
